/**
 * Vesper Event Service (Shared)
 */

import apiClient from './api.client';
import { API } from '../constants/api';
import type { Event, EventDetailResponse, CreateEventPayload } from '../types/event.types';

export async function createEvent(payload: CreateEventPayload): Promise<Event> {
  const res = await apiClient.post<{ data: Event }>(API.EVENTS, payload);
  return res.data.data;
}

export async function getMyEvents(): Promise<Event[]> {
  const res = await apiClient.get<{ data: Event[] }>(API.EVENTS);
  return res.data.data;
}

export async function getEventById(id: string): Promise<EventDetailResponse> {
  const res = await apiClient.get<{ data: EventDetailResponse }>(API.EVENT_BY_ID(id));
  return res.data.data;
}

export async function inviteMember(eventId: string, userId: string): Promise<void> {
  await apiClient.post(API.EVENT_INVITE(eventId), { userId });
}

export async function respondToInvite(
  eventId: string,
  response: 'accepted' | 'declined',
): Promise<void> {
  await apiClient.post(API.EVENT_RESPOND(eventId), { response });
}

export async function swapMember(
  eventId: string,
  removeUserId: string,
  addUserId: string,
): Promise<void> {
  await apiClient.post(API.EVENT_SWAP(eventId), { removeUserId, addUserId });
}

export async function cancelMembership(eventId: string): Promise<void> {
  await apiClient.post(API.EVENT_CANCEL(eventId));
}

export async function cancelEntireEvent(eventId: string): Promise<void> {
  await apiClient.post(API.EVENT_CANCEL_EVENT(eventId));
}

export async function completeEvent(eventId: string): Promise<void> {
  await apiClient.post(API.EVENT_COMPLETE(eventId));
}

export interface ReplacementCandidate {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  matchScore: number;
  isAlternate: boolean;
}

export async function getReplacementCandidates(eventId: string): Promise<{ alternates: ReplacementCandidate[]; benchFriends: ReplacementCandidate[] }> {
  const res = await apiClient.get<{ data: { alternates: ReplacementCandidate[]; benchFriends: ReplacementCandidate[] } }>(API.EVENT_REPLACEMENT_CANDIDATES(eventId));
  return res.data.data;
}

export async function inviteReplacement(eventId: string, userId: string, replacesUserId?: string): Promise<void> {
  await apiClient.post(API.EVENT_INVITE_REPLACEMENT(eventId), { userId, replacesUserId });
}

export async function acceptReplacement(eventId: string): Promise<void> {
  await apiClient.post(API.EVENT_ACCEPT_REPLACEMENT(eventId));
}

export async function getSuggestedMembers(
  eventId: string,
  options?: { radiusMiles?: number; excludeIds?: string[] },
): Promise<unknown[]> {
  const params = new URLSearchParams();
  if (options?.radiusMiles) params.set('radiusMiles', String(options.radiusMiles));
  if (options?.excludeIds?.length) params.set('excludeIds', options.excludeIds.join(','));
  const res = await apiClient.get<{ data: unknown[] }>(
    `${API.EVENT_SUGGESTIONS(eventId)}?${params}`,
  );
  return res.data.data;
}

// ── Attendance (Phase 9) ─────────────────────────────────────────────────────

export interface AttendanceStatus {
  eventId: string;
  eventStartTime: string;
  eventEndTime: string;
  punishFlaking: boolean;
  attended: boolean | null;
  checkedInAt: string | null;
  checkInMethod: 'geofence' | 'peer_confirm' | 'manual' | null;
  inCheckInWindow: boolean;
  radiusMeters: number;
}

/** Fetch the current viewer's check-in status for an event (private or public). */
export async function getMyAttendanceStatus(eventId: string): Promise<AttendanceStatus> {
  const res = await apiClient.get<{ data: AttendanceStatus }>(API.EVENT_ATTENDANCE_ME(eventId));
  return res.data.data;
}

/** Host-only: manually mark an attendee as attended / no-show. */
export async function manualCheckIn(
  eventId: string,
  userId: string,
  attended: boolean,
): Promise<void> {
  await apiClient.post(API.EVENT_MANUAL_CHECK_IN(eventId, userId), { attended });
}
